import { Skeleton } from 'antd';
import { Valuation as ValuationComponent, PostOffer, Notes } from 'components/common';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { BankDetailsPage } from 'pages/BankDetailsPage';
import { CaseOverview } from './Overview';
import { CaseFinancingPage } from './Financing';
import { CaseApplicantsPage } from './Applicants';
import { CaseExpensesPage } from './Expenses';
import { CasePropertyPage } from './Property';
import { CaseProductPage } from './Product';
import { CaseAffordabilityPage } from './Affordability';
import { CaseSolicitorsPage } from './Solicitors';
import { OoCustomerFilesPage } from './CustomerFiles';
import { OoEvidencePage } from './Evidence';
import { CaseSubmitPage } from './Submit';
import type { OoFormRouteContext } from './types';

interface OoFormSectionContentProps {
  currentSection: CaseSidebarItemId;
  context: OoFormRouteContext;
}

export function OoFormSectionContent({ currentSection, context }: OoFormSectionContentProps) {
  switch (currentSection) {
    case CaseSidebarItemId.OVERVIEW:
      return (
        <CaseOverview
          case={context.case}
          isLoading={context.isCaseLoading}
          canAccessHppPages={context.sidebarContext.canAccessHppPages}
        />
      );
    case CaseSidebarItemId.FINANCING:
      return (
        <CaseFinancingPage
          caseId={context.caseId}
          case={context.case}
          isCaseLoading={context.isCaseLoading}
          sidebarContext={context.sidebarContext}
          canAccessHppPages={context.sidebarContext.canAccessHppPages}
        />
      );
    case CaseSidebarItemId.APPLICANTS:
      return (
        <CaseApplicantsPage
          case={context.case}
          isCaseLoading={context.isCaseLoading}
          sidebarContext={context.sidebarContext}
        />
      );
    case CaseSidebarItemId.EXPENSES:
      return (
        <CaseExpensesPage
          caseId={context.caseId}
          case={context.case}
          isCaseLoading={context.isCaseLoading}
          sidebarContext={context.sidebarContext}
        />
      );
    case CaseSidebarItemId.PROPERTY:
      return <CasePropertyPage caseId={context.caseId} sidebarContext={context.sidebarContext} />;
    case CaseSidebarItemId.SOLICITORS:
      if (!context.sidebarContext.canAccessSolicitorsPage) {
        return null;
      }

      return <CaseSolicitorsPage caseId={context.caseId} sidebarContext={context.sidebarContext} />;
    case CaseSidebarItemId.PRODUCT:
      if (!context.sidebarContext.canAccessHppPages) {
        return null;
      }

      return <CaseProductPage routeContext={context} />;
    case CaseSidebarItemId.AFFORDABILITY_CHECK:
      if (!context.sidebarContext.canAccessHppPages) {
        return null;
      }

      return <CaseAffordabilityPage routeContext={context} />;
    case CaseSidebarItemId.SUBMIT_DIP:
      if (!context.sidebarContext.canAccessSubmissionPages) {
        return null;
      }

      return (
        <CaseSubmitPage
          caseId={context.caseId}
          caseData={context.case}
          isCaseLoading={context.isCaseLoading}
          sidebarContext={context.sidebarContext}
          variant='DIP'
        />
      );
    case CaseSidebarItemId.SUBMIT_APPLICATION:
      if (!context.sidebarContext.canAccessSubmissionPages) {
        return null;
      }

      return (
        <CaseSubmitPage
          caseId={context.caseId}
          caseData={context.case}
          isCaseLoading={context.isCaseLoading}
          sidebarContext={context.sidebarContext}
          variant='APPLICATION'
        />
      );
    case CaseSidebarItemId.CUSTOMER_FILES:
      return (
        <OoCustomerFilesPage
          caseId={context.caseId}
          isBankDetailsComplete={context.isBankDetailsComplete}
          onGoToEvidence={context.onGoToEvidence}
          onGoToBankDetails={context.onGoToBankDetails}
        />
      );
    case CaseSidebarItemId.EVIDENCE:
      return <OoEvidencePage caseId={context.caseId} />;
    case CaseSidebarItemId.BANK_DETAILS:
      return (
        <BankDetailsPage
          applicationId={context.caseId}
          bankAccount={context.bankAccount}
          isLoading={context.isCaseLoading}
          openInEditMode={context.openBankDetailsInEditMode}
          onOpenInEditModeConsumed={context.onOpenBankDetailsInEditModeConsumed}
        />
      );
    case CaseSidebarItemId.VALUATION:
      return <ValuationComponent valuation={context.valuation} />;
    case CaseSidebarItemId.POST_OFFER:
      return context.isConveyancingLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <PostOffer conveyancing={context.conveyancing} />
      );
    case CaseSidebarItemId.COMMUNICATION:
      return (
        <div className='mt-6'>
          <Notes
            notes={context.formattedNotes}
            onCreateNote={context.onCreateNote}
            isLoading={context.isCreatingNote}
          />
        </div>
      );
    default:
      return null;
  }
}
